import { type ComponentType, render } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import type { ThemeCatalog } from "../sdk";
import type { TabBarProps } from "../sdk/theme";
import { httpsAddress } from "../shared/hosts";
import { ApiFailure, api, setUnauthenticatedHandler } from "./api";
import { type Boot, retryDelay, signedOut } from "./boot";
import { loadChunk } from "./chunks";
import { AdvancedSearch } from "./components/AdvancedSearch";
import { Avatar } from "./components/Avatar";
import { CommandPalette } from "./components/CommandPalette";
import { Composers } from "./components/Composer";
import { ContextMenu } from "./components/ContextMenu";
import { DashboardShell } from "./components/DashboardShell";
import { DesignFrame, useDesignFrame } from "./components/DesignFrame";
import { Icon, type IconName } from "./components/Icon";
import { Logo } from "./components/Logo";
import { PasswordDialog } from "./components/PasswordDialog";
import { Sidebar } from "./components/Sidebar";
import { Empty, IconButton, Menu, Modal, Spinner, Toasts } from "./components/ui";
import { useLayout } from "./layout";
import { connectLive, followMailbox, live } from "./live";
import { loadModules, useAppPages, useModulesLoaded, useOverlays } from "./modules";
import { offline, sendQueued, startOffline } from "./offline";
import { overridable } from "./overrides";
import { Login, Setup } from "./pages/Login";
import { MailPage } from "./pages/MailPage";
import { parseThemeFile } from "./pages/ThemeEditor";
import { startPwa } from "./pwa";
import {
  dashboardOf,
  legacyDashboardHref,
  type MailSource,
  mailboxHome,
  mailboxOf,
  mailHash,
  navigate,
  parseDashboardRoute,
  parseRoute,
  safeDecode,
  setCurrentMailbox,
  sourcePath,
  UNIFIED_INBOX,
  useHash,
} from "./router";
import { applyTheme, SHORTCUTS, setCustomTheme, setThemeCatalog } from "./settings-shared";
import { signOut } from "./sign-out";
import { runSearch } from "./smart-search";
import {
  app,
  errorToast,
  loadAccount,
  openComposer,
  refreshFolders,
  refreshSearches,
  switchMailbox,
  useFields,
  useStore,
} from "./store";
import { isThemePreview } from "./theme-preview";

/**
 * A page that loads on first use, so starting the app doesn't wait for it. Shows a spinner
 * meanwhile; `preload` fetches it ahead of time (and the service worker keeps it for offline).
 */
const lazyPage = <P extends object>(load: () => Promise<ComponentType<P>>) => {
  let loaded: ComponentType<P> | null = null;
  let loading: Promise<ComponentType<P>> | null = null;
  const preload = () => {
    loading ??= loadChunk(load).then(
      (page) => {
        loaded = page;
        return page;
      },
      (error: unknown) => {
        // Try again next time, say once back online.
        loading = null;
        throw error;
      },
    );
    return loading;
  };
  const Page = (props: P) => {
    const [, setReady] = useState(loaded !== null);
    const [failed, setFailed] = useState(false);
    useEffect(() => {
      if (!loaded)
        preload().then(
          () => setReady(true),
          () => setFailed(true),
        );
    }, []);
    if (loaded) {
      const Loaded = loaded;
      return <Loaded {...props} />;
    }
    return failed ? (
      <Empty
        icon="junk"
        title="Could not load this page"
        text="Check your connection and try again."
      />
    ) : (
      <Spinner />
    );
  };
  return { Page, preload };
};

const settingsPage = lazyPage(() => import("./pages/SettingsPage").then((m) => m.SettingsPage));
const adminPage = lazyPage(() => import("./pages/AdminPage").then((m) => m.AdminPage));
const domainAdminPage = lazyPage(() =>
  import("./pages/DomainAdminPage").then((m) => m.DomainAdminPage),
);
const contactsPage = lazyPage(() => import("./pages/ContactsPage").then((m) => m.ContactsPage));
const SettingsPage = settingsPage.Page;
const AdminPage = adminPage.Page;
const DomainAdminPage = domainAdminPage.Page;
const ContactsPage = contactsPage.Page;

const SearchBox = ({ current }: { current: string }) => {
  const [value, setValue] = useState(current);
  const [advanced, setAdvanced] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => setValue(current), [current]);
  return (
    <form
      class="search"
      data-part="search"
      role="search"
      onSubmit={(event) => {
        event.preventDefault();
        if (busy) return;
        setBusy(true);
        void runSearch(value).finally(() => setBusy(false));
      }}
    >
      {busy ? <Spinner size="small" /> : <Icon name="search" size={17} />}
      <input
        type="search"
        placeholder="Search mail, or describe it: PDFs from Bob last week"
        aria-label="Search mail"
        value={value}
        onInput={(event) => setValue((event.target as HTMLInputElement).value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") (event.target as HTMLInputElement).blur();
        }}
      />
      {value && (
        <button
          type="button"
          class="icon-button"
          aria-label="Clear search"
          onClick={() => {
            setValue("");
            navigate(mailHash("#/"));
          }}
        >
          <Icon name="x" size={15} />
        </button>
      )}
      <button
        type="button"
        class="icon-button search-advanced"
        aria-label="Search options"
        aria-expanded={advanced}
        title="Search options"
        onClick={() => setAdvanced(!advanced)}
      >
        <Icon name="filter" size={16} />
      </button>
      {advanced && <AdvancedSearch query={value} onClose={() => setAdvanced(false)} />}
    </form>
  );
};

/** Says when the mail on screen is a saved copy, and how many changes wait to go out. */
const OfflineBanner = () => {
  const { active, pending } = useStore(offline, (state) => state);
  if (!active) return null;
  return (
    <div class="offline-banner" role="status">
      <Icon name="cloudOff" size={15} />
      <span>
        Offline: showing saved mail
        {pending > 0 && ` · ${pending} ${pending === 1 ? "change" : "changes"} waiting to send`}
      </span>
    </div>
  );
};

const DefaultTabBar = ({ page }: TabBarProps) => {
  const tab = (label: string, icon: IconName, active: boolean, onClick: () => void) => (
    <button
      type="button"
      class={`tab${active ? " active" : ""}`}
      aria-current={active ? "page" : undefined}
      onClick={onClick}
    >
      <Icon name={icon} size={22} />
      <span>{label}</span>
    </button>
  );
  return (
    <nav class="tabbar" data-part="tabbar" aria-label="Sections">
      {tab("Mail", "inbox", page === "mail", () => navigate(mailHash("#/")))}
      {useAppPages().some((p) => p.id === "files") &&
        tab("Files", "folder", page === "module" && location.hash.includes("/files"), () =>
          navigate(mailHash("#/files")),
        )}
      {tab("Contacts", "book", page === "contacts", () => navigate("#/contacts"))}
      {tab("Settings", "settings", page === "settings" || page === "admin", () =>
        navigate("#/settings/general"),
      )}
    </nav>
  );
};
const FloatingActions = ({ files, path }: { files: boolean; path: string[] }) => {
  const state = useStore(
    app,
    (s) => ({
      readonly: !!s.mailbox && s.mailboxes.find((b) => b.id === s.mailbox)?.role === "read",
      composing: s.composers.length > 0,
    }),
    (a, b) => a.readonly === b.readonly && a.composing === b.composing,
  );
  if (state.composing || (files && ["trash", "shared", "images"].includes(path[0] ?? "")))
    return null;
  return (
    <div class="floating-action-area" data-part="floating-actions">
      {files ? (
        <Menu
          align="right"
          submenuNavigation
          trigger={({ open, toggle }) => (
            <button
              type="button"
              class="floating-action"
              aria-label="New"
              aria-expanded={open}
              aria-haspopup="menu"
              disabled={state.readonly}
              onClick={toggle}
            >
              <Icon name="plus" size={24} />
              <span>New</span>
            </button>
          )}
          items={[
            {
              label: "Upload files",
              icon: "upload",
              onSelect: () =>
                window.dispatchEvent(
                  new CustomEvent("cloudmail:files-action", { detail: "upload" }),
                ),
            },
            {
              label: "New folder",
              icon: "folder",
              onSelect: () =>
                window.dispatchEvent(
                  new CustomEvent("cloudmail:files-action", { detail: "folder" }),
                ),
            },
          ]}
        />
      ) : (
        <button
          type="button"
          class="floating-action"
          data-part="compose-button"
          aria-label="Compose"
          disabled={state.readonly}
          onClick={() => openComposer({ mode: "new", sourceId: null })}
        >
          <Icon name="pencil" size={24} />
          <span>Compose</span>
        </button>
      )}
    </div>
  );
};
const TabBar = overridable("TabBar", DefaultTabBar);

/** The frame: top bar, sidebar, the page, and what floats over them. */
const AppShell = overridable("AppShell", ({ header, banner, sidebar, children, footer }) => {
  const frame = useDesignFrame();
  if (frame)
    return (
      <div class="shell designed-shell" data-part="shell">
        <DesignFrame
          node={frame}
          slots={{
            header,
            banner,
            sidebar,
            main: (
              <main class="content" data-part="content">
                {children}
              </main>
            ),
            footer,
          }}
        />
      </div>
    );
  return (
    <div class="shell" data-part="shell">
      {header}
      {banner}
      <div class="workspace" data-part="workspace">
        {sidebar}
        <main class="content" data-part="content">
          {children}
        </main>
      </div>
      {footer}
    </div>
  );
});

/** A page a module adds (#/<id>); until modules load it waits, then says if it isn't there. */
const ModulePage = ({ id, path }: { id: string; path: string[] }) => {
  const pages = useAppPages();
  const ready = useModulesLoaded();
  const Page = pages.find((page) => page.id === id)?.component;
  if (Page) return <Page path={path} />;
  if (!ready) return <Spinner />;
  return (
    <Empty
      icon="search"
      title="This page isn't here"
      text="It may belong to a module that is turned off."
    />
  );
};

const Shell = () => {
  const overlays = useOverlays();
  const hash = useHash();
  // Only what the frame itself uses: a toast or a composer re-renders just its own part.
  const { me, folders, mailbox: loadedMailbox } = useFields(app, "me", "folders", "mailbox");
  const [help, setHelp] = useState(false);
  const [palette, setPalette] = useState(false);
  const route = parseRoute(hash);
  const legacyDashboard = legacyDashboardHref(hash);
  useEffect(() => {
    if (legacyDashboard)
      location.replace(
        isThemePreview()
          ? `/theme-preview${legacyDashboard.replace("#", "?theme-preview=1#")}`
          : legacyDashboard,
      );
  }, [legacyDashboard]);
  const layout = useLayout();
  // The shared mailbox in the address (or null). Set before children render so their API
  // calls carry it.
  const mailbox = mailboxOf(hash);
  setCurrentMailbox(mailbox);

  // Realtime updates for the mailbox on screen: one connection, which follows the mailbox as it
  // changes (each message the unified inbox opens is in its own).
  useEffect(() => connectLive(mailbox), []);
  useEffect(() => followMailbox(mailbox), [mailbox]);

  // Switching mailboxes swaps the folder list.
  useEffect(() => {
    if (app.get().mailbox === mailbox && app.get().folders.length > 0) return;
    // Saved searches change only from Search and the sidebar, so they load here and at boot.
    void refreshSearches();
    // Back to a mailbox shown a moment ago, and nothing changed since: as it was.
    if (switchMailbox(mailbox, live.get().connected)) return;
    app.set({ folders: [], labels: [] });
    void refreshFolders().catch((error: unknown) => {
      errorToast(error);
      if (mailbox) navigate("#/", true);
    });
  }, [mailbox]);

  // Land in the Inbox; turn mailto: links into a composer.
  useEffect(() => {
    if (hash.startsWith("#/compose")) {
      const to =
        (safeDecode(hash.replace(/^#\/compose\/?/, "")) ?? "")
          .replace(/^mailto:/i, "")
          .split("?")[0] ?? "";
      openComposer({ mode: "new", sourceId: null, ...(to ? { to: to.split(",") } : {}) });
      const inbox = folders.find((folder) => folder.role === "inbox");
      if (inbox) navigate(`#/mail/${inbox.id}`, true);
      return;
    }
    if (route === null && folders.length > 0 && loadedMailbox === mailbox) {
      const inbox = folders.find((folder) => folder.role === "inbox");
      if (inbox) navigate(mailHash(`#/mail/${inbox.id}`), true);
    }
  }, [hash, folders.length, loadedMailbox]);

  // Close the mobile mailbox drawer whenever the user goes somewhere.
  useEffect(() => {
    app.set({ navOpen: false });
  }, [hash]);

  useEffect(() => {
    // "g" then a letter jumps somewhere, as in Gmail.
    let pendingG = 0;
    const jump = (key: string) => {
      const { folders, mailboxes, me } = app.get();
      // Digits switch mailboxes: 1–9 by their place in the switcher, 0 for all inboxes.
      if (/^[0-9]$/.test(key) && mailboxes.length > 1) {
        if (key === "0") {
          if (me?.settings.unifiedInbox ?? false) navigate(UNIFIED_INBOX);
          return;
        }
        const box = mailboxes[Number(key) - 1];
        if (box) navigate(mailboxHome(box.kind === "person" ? null : box.id));
        return;
      }
      const folder = (role: string) => folders.find((entry) => entry.role === role);
      const targets: Record<string, string | undefined> = {
        i: folder("inbox") && sourcePath({ kind: "folder", id: folder("inbox")?.id ?? "" }),
        t: folder("sent") && sourcePath({ kind: "folder", id: folder("sent")?.id ?? "" }),
        d: folder("drafts") && sourcePath({ kind: "folder", id: folder("drafts")?.id ?? "" }),
        s: sourcePath({ kind: "view", id: "flagged" }),
        a: sourcePath({ kind: "view", id: "all" }),
        c: "#/contacts",
      };
      const target = targets[key];
      if (target) navigate(target);
    };
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setPalette((open) => !open);
        return;
      }
      if ((event.target as HTMLElement).closest("input, textarea, select, [contenteditable=true]"))
        return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (pendingG && Date.now() - pendingG < 1500) {
        pendingG = 0;
        jump(event.key.toLowerCase());
        return;
      }
      if (event.key === "g") pendingG = Date.now();
      if (event.key === "?") setHelp(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!me) return null;
  if (legacyDashboard) return <Spinner />;
  const source: MailSource | null = route?.page === "mail" ? route.source : null;

  const header = (
    <header class="topbar" data-part="topbar">
      <IconButton
        icon="menu"
        label="Mailboxes"
        class="menu-button"
        onClick={() => app.set((state) => ({ navOpen: !state.navOpen }))}
      />
      <a class="brand" data-part="brand" href="#/" aria-label={me.appName}>
        <Logo appName={me.appName} size={30} />
      </a>
      <SearchBox current={source?.kind === "search" ? source.query : ""} />
      <IconButton
        icon="settings"
        label="Settings"
        class="hide-mobile"
        onClick={() => navigate("#/settings/general")}
      />
      <Menu
        align="right"
        trigger={({ open, toggle }) => (
          <button
            type="button"
            class="avatar-button"
            aria-label="Account"
            aria-haspopup="menu"
            aria-expanded={open}
            onClick={toggle}
          >
            <Avatar person={{ name: me.name, address: me.login }} size="small" />
          </button>
        )}
        items={[
          { heading: me.login },
          { label: "Keyboard shortcuts", icon: "keyboard", onSelect: () => setHelp(true) },
          ...(me.isAdmin || (me.managedDomains ?? []).length > 0
            ? [{ separator: true as const }]
            : []),
          ...(me.isAdmin
            ? [
                {
                  label: "Admin",
                  icon: "shield" as const,
                  onSelect: () => location.assign("/admin"),
                },
              ]
            : []),
          ...((me.managedDomains ?? []).length > 0
            ? [
                {
                  label: "Domain admin",
                  icon: "globe" as const,
                  onSelect: () => location.assign("/domain-admin"),
                },
              ]
            : []),
          { separator: true },
          {
            label: "Sign out",
            icon: "logout",
            onSelect: signOut,
          },
        ]}
      />
    </header>
  );

  return (
    <AppShell
      layout={layout}
      header={header}
      banner={<OfflineBanner />}
      sidebar={<Sidebar current={source} />}
      footer={
        <>
          <TabBar page={route?.page} />
          <FloatingActions
            files={route?.page === "module" && route.id === "files"}
            path={route?.page === "module" ? route.path : []}
          />
          <Composers />
          {overlays.map((Overlay, index) => (
            <Overlay key={index} />
          ))}
          <PasswordDialog />
          <Toasts />
          <ContextMenu />
          {palette && <CommandPalette onClose={() => setPalette(false)} />}
          {help && (
            <Modal title="Keyboard shortcuts" onClose={() => setHelp(false)}>
              <table class="shortcuts">
                <tbody>
                  {SHORTCUTS.map(([key, label]) => (
                    <tr key={key}>
                      <td>
                        <kbd>{key}</kbd>
                      </td>
                      <td>{label}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Modal>
          )}
        </>
      }
    >
      {route?.page === "mail" && <MailPage source={route.source} open={route.open} />}
      {route?.page === "contacts" && <ContactsPage />}
      {route?.page === "module" && <ModulePage id={route.id} path={route.path} />}
      {route?.page === "settings" && <SettingsPage tab={route.tab} path={route.path} />}
      {route === null && <Spinner />}
    </AppShell>
  );
};

const App = () => {
  const dashboard = dashboardOf(location.pathname);
  const hash = useHash();
  const [boot, setBoot] = useState<Boot>({ state: "loading" });
  /** Tries to start refused in a row (429), so each next one waits longer. */
  const refusals = useRef(0);

  /** Says why the start failed and tries again: shortly, or later after each refusal. */
  const retry = (failure: unknown, signedIn: boolean) => {
    errorToast(failure);
    const known = failure instanceof ApiFailure ? failure : null;
    const delay = retryDelay(known, refusals.current);
    refusals.current = known?.status === 429 ? refusals.current + 1 : 0;
    setTimeout(() => void start(signedIn), delay);
  };

  /** `signedIn`: right after signing in (or setting up), so modules can set this device up again
   * for the new session (notifications, for one). */
  const start = async (signedIn = false) => {
    try {
      // The person and the folders of the mailbox in the address, side by side.
      const { me, showFolders } = await loadAccount(location.hash, { mail: dashboard === null });
      refusals.current = 0;
      setThemeCatalog(me.themes);
      setCustomTheme(me.settings.customTheme ?? null);
      applyTheme(me.settings.theme, me.settings.accent);
      void loadModules(me.modules ?? [], signedIn, { surface: dashboard ?? "mail" });
      setBoot({ state: "ready" });
      if (dashboard) return;
      showFolders();
      void refreshSearches();
      // The other pages load once the mail is up, so they open at once (and work offline).
      const idle = window.requestIdleCallback ?? ((run: () => void) => setTimeout(run, 2000));
      idle(() => {
        for (const page of [settingsPage, contactsPage]) void page.preload().catch(() => {});
      });
      // Changes made offline last time go out now.
      if (!isThemePreview()) void sendQueued();
      try {
        navigator.registerProtocolHandler?.("mailto", `${location.origin}/#/compose/%s`);
      } catch {}
    } catch (error) {
      if (error instanceof ApiFailure && error.status === 401) {
        const setup = await api
          .get<{
            needsSetup: boolean;
            tokenConfigured: boolean;
            appName: string;
            theme: string | null;
            themes: ThemeCatalog;
          }>("/auth/setup")
          // Offline, or refused: try again.
          .catch((failure: unknown) => {
            retry(failure, signedIn);
            return null;
          });
        if (!setup) return;
        refusals.current = 0;
        document.title = setup.appName;
        // The sign-in page wears the deployment's (or this web address's domain's) theme.
        setThemeCatalog(setup.themes);
        applyTheme(setup.theme ?? "system");
        setBoot(
          setup.needsSetup
            ? { state: "setup", appName: setup.appName, tokenConfigured: setup.tokenConfigured }
            : { state: "signed-out", appName: setup.appName },
        );
      } else {
        retry(error, signedIn);
      }
    }
  };

  useEffect(() => {
    setUnauthenticatedHandler(() =>
      setBoot((current) => signedOut(current, app.get().me?.appName ?? "Cloudmail")),
    );
    void start();
  }, []);

  switch (boot.state) {
    case "loading":
      return (
        <div class="boot">
          <Spinner />
        </div>
      );
    case "setup":
      return (
        <Setup
          appName={boot.appName}
          tokenConfigured={boot.tokenConfigured}
          onDone={() => void start(true)}
        />
      );
    case "signed-out":
      return (
        <>
          <Login appName={boot.appName} onDone={() => void start(true)} />
          <Toasts />
        </>
      );
    case "ready":
      if (dashboard) {
        const route = parseDashboardRoute(dashboard, hash);
        return (
          <DashboardShell dashboard={dashboard}>
            {dashboard === "admin" ? (
              <AdminPage tab={route.tab} path={route.path} />
            ) : (
              <DomainAdminPage tab={route.tab} path={route.path} />
            )}
          </DashboardShell>
        );
      }
      return <Shell />;
  }
};

// Opened over plain http (a link written with http://), the app moves to https first: its
// sign-in cookie is only kept there.
const secure = httpsAddress(location.href);
if (secure) location.replace(secure);
else {
  const legacyDashboard = dashboardOf(location.pathname)
    ? null
    : legacyDashboardHref(location.hash);
  if (legacyDashboard)
    location.replace(
      isThemePreview()
        ? `/theme-preview${legacyDashboard.replace("#", "?theme-preview=1#")}`
        : legacyDashboard,
    );
  else {
    if (!dashboardOf(location.pathname)) {
      if (!isThemePreview()) {
        startPwa();
        startOffline();
      }
    }
    const root = document.getElementById("app");
    if (root) render(<App />, root);
  }
}

if (isThemePreview()) {
  window.addEventListener("message", (event) => {
    if (
      event.origin !== location.origin ||
      event.source !== window.parent ||
      event.data?.type !== "cloudmail-theme-preview"
    )
      return;
    const me = app.get().me;
    if (!me) return;
    try {
      const theme = parseThemeFile(JSON.stringify(event.data.theme));
      const mode = ["light", "dark", "oled"].includes(event.data.mode) ? event.data.mode : "dark";
      app.set({
        me: {
          ...me,
          settings: { ...me.settings, theme: "custom", customTheme: theme, layout: {} },
        },
      });
      setCustomTheme(theme);
      applyTheme("custom", "theme", { ...me.settings.appearance, mode, customAccent: null });
      window.parent.postMessage({ type: "cloudmail-theme-applied" }, location.origin);
    } catch {
      /* An invalid preview never changes the frame. */
    }
  });
}
